import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  IconButton,
  Button,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { onAuthStateChanged, User } from 'firebase/auth';
import {
  doc,
  getDoc,
  collection,
  getDocs,
  updateDoc,
  addDoc,
  deleteDoc,
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { styles } from './styles';
import { IAddress } from 'utils/types/User';
import { firestore, auth } from 'services/firebase/client';

const MyAddresses: React.FC = () => {
  const navigate = useNavigate();

  const [user, setUser] = useState<User | null>(null);
  const [mainAddress, setMainAddress] = useState<IAddress | null>(null);
  const [addresses, setAddresses] = useState<IAddress[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async usr => {
      setUser(usr);
      if (usr) {
        try {
          setLoading(true);

          // Load main address
          const docRef = doc(firestore, 'subscription_users', usr.uid);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            if (data.address) {
              setMainAddress({ id: 'main', ...data.address });
            }
          }

          // Load address collection
          const colRef = collection(
            firestore,
            'subscription_users',
            usr.uid,
            'addresses',
          );
          const snapshot = await getDocs(colRef);
          const list: IAddress[] = snapshot.docs.map(doc => ({
            id: doc.id,
            ...(doc.data() as Omit<IAddress, 'id'>),
          }));
          setAddresses(list);
        } catch (err) {
          console.error('Erro ao buscar endereços:', err);
        } finally {
          setLoading(false);
        }
      }
    });
    return () => unsub();
  }, []);

  const handleMakePrimary = async (addr: IAddress) => {
    if (!user) return;
    const userRef = doc(firestore, 'subscription_users', user.uid);
    const colRef = collection(
      firestore,
      'subscription_users',
      user.uid,
      'addresses',
    );

    try {
      setLoading(true);
      // Se o endereço principal atual existir, salve ele na subcoleção (se ainda não estiver lá)
      if (mainAddress && mainAddress.id !== 'main') {
        const exists = addresses.some(a => a.id === mainAddress.id);
        if (!exists) {
          await addDoc(colRef, mainAddress);
        }
      }
      // Atualiza o endereço principal na raíz
      const { id, ...addrData } = addr;
      await updateDoc(userRef, { address: addrData });
      setMainAddress(addr);

      // Remove o novo principal da subcoleção
      if (!addr.id || typeof addr.id !== 'string') {
        throw new Error('Endereço sem ID não pode ser removido.');
      }
      // addr.id is guaranteed to be a string here
      const addrRef = doc(
        firestore,
        'subscription_users',
        user.uid,
        'addresses',
        addr.id as string,
      );
      await deleteDoc(addrRef);
      setAddresses(prev => prev.filter(a => a.id !== addr.id));
    } catch (error) {
      console.error('Erro ao definir como principal:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (addr: IAddress) => {
    if (!user || !addr) return;
    if (!addr.id || typeof addr.id !== 'string') {
      throw new Error('Endereço sem ID não pode ser removido.');
    }
    try {
      setLoading(true);
      const addrRef = doc(
        firestore,
        'subscription_users',
        user.uid,
        'addresses',
        addr.id,
      );
      await deleteDoc(addrRef);
      setAddresses(prev => prev.filter(a => a.id !== addr.id));
    } catch (err) {
      console.error('Erro ao excluir endereço:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    navigate('/add-address');
  };

  if (loading) {
    return (
      <Box sx={styles.loading}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      <Typography variant="h6">Endereço Principal</Typography>
      {mainAddress ? (
        <Paper sx={styles.addressPaper}>
          <Typography>
            <strong>{mainAddress.recipient}</strong>
          </Typography>
          <Typography>
            {mainAddress.street}, {mainAddress.number}
          </Typography>
          <Typography>
            {mainAddress.neighborhood}, {mainAddress.city}
          </Typography>
          <Typography>CEP: {mainAddress.zip}</Typography>
        </Paper>
      ) : (
        <Typography>Nenhum endereço principal definido.</Typography>
      )}

      <Box textAlign="right" my={2}>
        <Button variant="contained" color="primary" onClick={handleAddNew}>
          Adicionar novo endereço
        </Button>
      </Box>

      <Typography variant="h6" sx={styles.sectionTitle}>
        Outros endereços
      </Typography>
      {addresses.length === 0 ? (
        <Typography>Nenhum endereço adicional cadastrado.</Typography>
      ) : (
        <Grid container spacing={2}>
          {addresses.map(addr => (
            <Grid item xs={12} md={6} key={addr.id}>
              <Paper sx={styles.addressPaper}>
                <Typography>
                  <strong>{addr.recipient}</strong>
                </Typography>
                <Typography>
                  {addr.street}, {addr.number}
                </Typography>
                <Typography>
                  {addr.neighborhood}, {addr.city}
                </Typography>
                <Typography>CEP: {addr.zip}</Typography>
                <Box sx={styles.actionsBox}>
                  <Button
                    variant="text"
                    color="primary"
                    onClick={() => handleMakePrimary(addr)}
                  >
                    USAR ESTE ENDEREÇO
                  </Button>
                  <IconButton
                    aria-label="Excluir endereço"
                    onClick={() => handleDelete(addr)}
                    color="error"
                  >
                    <Delete />
                  </IconButton>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default MyAddresses;
